import React from 'react';
import { useSelector } from 'react-redux';
import 'react-select-v1/dist/react-select.css';
import { Link } from 'react-router-dom';
import { Site } from '@debbie/common/dist/index.js';
import { BlameLogRefType, BlameLogType } from '@debbie/common/dist/blamelog/index.js';
import { UpdateState, UpdateType } from '@debbie/common/dist/updates/index.js';
import { c } from '@debbie/cortex/dist/index.js';
import { blameLogs } from '@debbie/cortex/dist/api/index.js';
import * as updatesAPI from '@debbie/cortex/dist/api/updates.js';
import * as casesAPI from '@debbie/cortex/dist/api/cases.js';
import * as workflowsAPI from '@debbie/cortex/dist/api/workflows.js';
import { CopyToClipboard, Panel } from '..//index.js';
import THEME from '../../theme.js';
import Badge from '../Badge.js';
import BaseUpdate from '../BaseUpdate.js';
import PersonalDataRequest from '../PersonalDataRequest.js';
import HistItem from './HistItem.js';
import UpdateTypeLabel from '../UpdateTypeLabel.js';
import { isUUID } from '@debbie/common/dist/imports/data/helpers.js';
import { ExternalLink, Info, Pause, Zap } from '../Icons/index.js';
import { selectUpdateTypesMap } from '@debbie/cortex/dist/update-types/selectors.js';
import q from '@debbie/cortex/dist/query.js';
import { interactionsEntity } from '@debbie/cortex/dist/interactions/index.js';
import { caseVouchersEntity } from '@debbie/cortex/dist/case-vouchers/index.js';
import keys from '@debbie/common/dist/cache/keys.js';
import PerformedBy from '../performed-by/PerformedBy.js';
const UpdateItem = (props) => {
    const lang = useSelector((s) => s.lang);
    const site = useSelector((s) => s.site);
    const updateTypesMap = useSelector(selectUpdateTypesMap);
    const { historyItem, singleCase } = props;
    const options = [];
    if (site === Site.Caseworker) {
        options.push({
            label: lang.interactions.delete,
            onClick: async () => {
                await Promise.all(historyItem.data.map(async (update) => {
                    const blameLog = {
                        details: update,
                        refId: update.referenceId,
                        refType: BlameLogRefType.Case,
                        type: BlameLogType.DeleteUpdate,
                    };
                    await updatesAPI.deleteUpdate(update.updateId);
                    if (singleCase) {
                        await blameLogs.createBlameLog(blameLog, singleCase.tenantId);
                    }
                }));
                if (singleCase) {
                    await c.cases.getUpdates(singleCase.caseId);
                }
            },
        });
        if (singleCase &&
            historyItem.data[0]?.taskId &&
            historyItem.data[0]?.state === UpdateState.Executed) {
            options.push({
                label: lang.interactions.rollback,
                onClick: async () => {
                    await Promise.all(historyItem.data.map(async (update) => {
                        await workflowsAPI.rollback(singleCase.caseId, update.updateId);
                        const blameLog = {
                            details: { label: update.label },
                            refId: singleCase.caseId,
                            refType: BlameLogRefType.Case,
                            type: BlameLogType.WorkflowRollback,
                        };
                        blameLogs.createBlameLog(blameLog, singleCase.tenantId);
                    }));
                    await c.cases.getUpdates(singleCase.caseId);
                    q.invalidate([
                        ...keys.write.create(interactionsEntity), // TODO: Better way (basically invalidate because we dont know?)
                    ]);
                },
            });
        }
        // Undo case merge
        if (singleCase &&
            historyItem.data[0]?.data &&
            historyItem.data[0]?.data.isCaseMergeDetails &&
            !historyItem.data[0]?.data.undoed) {
            options.push({
                label: lang.mergeCases.undoCaseMerge,
                onClick: async () => {
                    await casesAPI.undoMerge(singleCase.caseId, historyItem.data[0].updateId);
                    const invalidateCaseCache = async (caseId) => {
                        await c.cases.getUpdates(caseId);
                        await c.cases.getById(caseId);
                        c.caseVouchers.getByCaseId(caseId);
                        q.invalidate([
                            ...keys.write.create(caseVouchersEntity), // TODO: This works, but perhaps find a more fine grained approach
                            ...keys.write.create(interactionsEntity),
                        ]);
                    };
                    // Invalidate to case cache
                    await invalidateCaseCache(singleCase.caseId);
                    // Invalidate from case cache
                    if (historyItem.data[0].data.fromCaseId) {
                        await invalidateCaseCache(historyItem.data[0].data.fromCaseId);
                    }
                },
            });
        }
    }
    if (historyItem.data[0].updateTypeId === UpdateType.SentToConnectPartner) {
        return null;
    }
    const isTaskUpdate = historyItem.data[0].taskId !== null;
    const isMigratedTask = historyItem.data[0].taskId &&
        !historyItem.data[0].byWorkflowId &&
        !isUUID(historyItem.data[0].taskId);
    const isActive = historyItem.data[0].continuedAt == null;
    const isPausing = updateTypesMap && updateTypesMap[historyItem.data[0].updateTypeId]
        ? updateTypesMap[historyItem.data[0].updateTypeId].pausing
        : false;
    return (React.createElement(HistItem, { type: "update", date: props.historyItem.date, timespan: props.historyItem.timespan, options: options, heading: React.createElement("span", null,
            historyItem.data[0]?.createdBy && (React.createElement(PerformedBy, { performedBy: historyItem.data[0].createdBy })),
            ' ',
            historyItem.data.length > 1 && (React.createElement(Badge, { style: { marginRight: '0.5em' }, theme: THEME.WARNING }, historyItem.data.length)),
            React.createElement(UpdateTypeLabel, { label: historyItem.data[0].label, updateTypeId: historyItem.data[0].updateTypeId }),
            ' ',
            isTaskUpdate ? (React.createElement(React.Fragment, null,
                historyItem.data[0].pausedAt ===
                    historyItem.data[0].continuedAt ? (React.createElement(Zap, { style: {
                        margin: '0.15em 0 -0.15em 0',
                        color: !isMigratedTask ? THEME.COLOR_ACCENT : undefined,
                    } })) : isPausing ? (React.createElement(Pause, { style: {
                        margin: '0.15em 0 -0.15em 0',
                        color: isActive ? THEME.COLOR_ACCENT : undefined,
                    } })) : (React.createElement(Info, { style: {
                        margin: '0.15em 0 -0.15em 0',
                        color: isActive ? THEME.COLOR_ACCENT : undefined,
                    } })),
                isMigratedTask && historyItem.data[0].taskId && (React.createElement(React.Fragment, null,
                    ' ',
                    React.createElement(CopyToClipboard, { style: {
                            fontWeight: '400',
                            fontSize: '0.8em',
                        }, text: historyItem.data[0].taskId }, historyItem.data[0].taskId))))) : (isActive &&
                (isPausing ? (React.createElement(Pause, { style: {
                        margin: '0.15em 0 -0.15em 0',
                        color: THEME.COLOR_ACCENT,
                    } })) : (React.createElement(Info, { style: {
                        margin: '0.15em 0 -0.15em 0',
                        color: THEME.COLOR_ACCENT,
                    } })))),
            historyItem.data[0].state === UpdateState.RolledBack && (React.createElement(React.Fragment, null,
                ' ',
                React.createElement(Badge, { style: { fontSize: '0.6em' }, theme: THEME.COLOR_GREY }, lang.workflows.rolledback)))) }, historyItem.data[0].updateTypeId ===
        UpdateType.DEBTORPERSONALDATAREQUEST ? (React.createElement(Panel, null,
        React.createElement(PersonalDataRequest, { item: historyItem.data[0] }))) : [UpdateType.CaseMergedFrom, UpdateType.CaseMergedTo].includes(historyItem.data[0].updateTypeId) ? (React.createElement(VisitCaseButton, { update: historyItem.data[0] })) : (React.createElement(BaseUpdate, { item: historyItem.data[0] }))));
};
export default UpdateItem;
const VisitCaseButton = (props) => {
    return (React.createElement("div", null,
        React.createElement(Panel, null, 
        // @ts-ignore
        React.createElement(Link, { to: `/cases/${props.update.update?.replace('#', '')}`, style: {
                color: 'inherit',
                textDecoration: 'none',
            } },
            props.update.update,
            ' ',
            React.createElement(ExternalLink, { style: {
                    margin: '0.1em 0 -0.1em 0',
                } })))));
};
//# sourceMappingURL=UpdateItem.js.map